import { Controller } from "@hotwired/stimulus"

// Guards the Art. 9 health-data consent checkbox. Unticking it (when consent
// was granted) will permanently erase the member's health data on save, so we
// ask for an explicit confirmation and revert the checkbox if the user cancels.
export default class extends Controller {
  static values = {
    granted: Boolean,
    title: String,
    message: String,
    confirmLabel: String,
    cancelLabel: String
  }

  toggle() {
    // Only the destructive transition (granted -> unchecked) needs a warning.
    if (this.element.checked || !this.grantedValue) return
    this.showModal()
  }

  showModal() {
    this.backdrop = document.createElement("div")
    this.backdrop.className = "fixed inset-0 z-50 flex items-center justify-center bg-black/50 transition-opacity"
    this.backdrop.setAttribute("aria-modal", "true")
    this.backdrop.setAttribute("role", "dialog")
    this.backdrop.innerHTML = `
      <div class="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-6 relative">
        <h3 class="text-lg font-bold mb-2">${this.escapeHtml(this.titleValue)}</h3>
        <p class="text-sm text-gray-600 dark:text-gray-300 mb-4">${this.escapeHtml(this.messageValue)}</p>
        <div class="flex justify-end gap-2">
          <button type="button" data-action="cancel"
            class="bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-2 px-4 rounded focus:outline-none focus:ring-2 focus:ring-gray-500">
            ${this.escapeHtml(this.cancelLabelValue)}
          </button>
          <button type="button" data-action="confirm"
            class="bg-red-600 hover:bg-red-700 focus:ring-red-500 text-white font-semibold py-2 px-4 rounded focus:outline-none focus:ring-2">
            ${this.escapeHtml(this.confirmLabelValue)}
          </button>
        </div>
      </div>
    `

    this.backdrop.addEventListener("click", (e) => {
      if (e.target === this.backdrop) this.cancel()
      if (e.target.dataset.action === "cancel") this.cancel()
      if (e.target.dataset.action === "confirm") this.accept()
    })

    document.addEventListener("keydown", this.handleKeydown)
    document.body.appendChild(this.backdrop)
  }

  handleKeydown = (e) => {
    if (e.key === "Escape") this.cancel()
  }

  // Keep the box unchecked: the withdrawal proceeds when the form is saved.
  accept() {
    this.closeModal()
  }

  // Restore the consent so nothing is erased.
  cancel() {
    this.element.checked = true
    this.closeModal()
  }

  closeModal() {
    document.removeEventListener("keydown", this.handleKeydown)
    if (this.backdrop) {
      this.backdrop.remove()
      this.backdrop = null
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
